import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  Renderer2,
  HostListener,
  AfterViewInit,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { ANGULAR_MODULES } from '../Shared/shared-imports';
import { WordReplacePipe } from '../Shared/word-replace.pipe';
import { take } from 'rxjs';
import { MarketInfoService } from '../services/marketInfo.service';
import { GeneralService } from '../services/general.service';
import { UserService } from '../services/User.service';
import { ASLicenseSettings } from '../Shared/model/GeneralModel';
import { QbMappingService } from 'src/app/services/qb-mapping.service';
import { PosSettingsService } from 'src/app/services/pos-settings.service';

@Component({
  selector: 'app-operations',
  standalone: true,
  imports: [ANGULAR_MODULES, WordReplacePipe],
  templateUrl: './operations.component.html',
  styleUrls: ['./operations.component.scss']
})
export class OperationsComponent implements OnChanges, AfterViewInit, OnInit {
  @Input() isCollapsed: boolean = false;

  /** Track which menus and submenus are open for icon rotation */
  openMenus: Set<string> = new Set();
  openSubMenus: Set<string> = new Set();
  marketName = ""
  enableInventory = false;
  userName = '';
  noLayout = false;
  useQboForCheckPrinting: boolean = false;

  constructor(
    private router: Router,
    private el: ElementRef,
    private renderer: Renderer2,
    private marketInfo: MarketInfoService,
    private generalService: GeneralService,
    private userService: UserService,
    private qbService: QbMappingService,
    private posSettingsService: PosSettingsService
  ) { }

  ngOnInit() {
    this.userName = this.userService.getLoggedInUserName();
    this.getMarketName();
    this.getASCloudLicenseSettings();
    this.loadPOSSettings();
  }

  ngAfterViewInit() {
    setTimeout(() => this.applyCollapsedState(this.isCollapsed), 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isCollapsed']) {
      setTimeout(() => this.applyCollapsedState(this.isCollapsed), 0);
    }
  }

  getASCloudLicenseSettings(): void {
    const licenseSettings = this.generalService.ASLicenseSettings;
    if (licenseSettings !== null) {
      this.enableInventory = licenseSettings.EnableInventoryControl ?? false;
      this.noLayout = licenseSettings.NoLayout ?? false;
      return;
    }

    this.generalService.getASCloudLicenseSettings().pipe(take(1)).subscribe({
      next: (res: ASLicenseSettings) => {
        this.generalService.ASLicenseSettings = res;
        this.enableInventory = res.EnableInventoryControl ?? false;
        this.noLayout = res.NoLayout ?? false;
      },
      error: () => {
        this.enableInventory = false;
        this.noLayout = false;
      }
    });
  }

  // loadPOSSettings(): void {
  //   this.qbService.getPosSettings()
  //     .pipe(take(1))
  //     .subscribe(res => {
  //       if (res) {
  //         this.useQboForCheckPrinting = res?.OtherSettings?.UseQboforCheckPrinting === true;
  //       }
  //     });
  // }

  loadPOSSettings(): void {
    this.qbService.getPosSettings()
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          const useQboForCheckPrinting = res?.OtherSettings?.UseQboforCheckPrinting === true;
          this.useQboForCheckPrinting = useQboForCheckPrinting;

          // Keep BehaviorSubject in sync with the DB value
          this.posSettingsService.setUseQboForCheckPrinting(useQboForCheckPrinting);
        }
      });
  }

  /** Hover to open/close entire sidebar */
  @HostListener('mouseenter')
  onMouseEnter() {
    this.isCollapsed = false;
    setTimeout(() => this.applyCollapsedState(false), 0);
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    this.isCollapsed = true;
    setTimeout(() => this.applyCollapsedState(true), 0);
  }

  getMarketName(): void {
    this.marketInfo.getMarketName().pipe(take(1)).subscribe({
      next: (res) => {
        this.marketName = res;
      }
    })
  }

  /** Apply collapsed sidebar state */
  private applyCollapsedState(collapsed: boolean) {
    let sidebar = this.el.nativeElement.querySelector('#sidebar') as HTMLElement | null;
    if (!sidebar) sidebar = document.getElementById('sidebar');
    if (!sidebar) return;

    if (collapsed) {
      this.renderer.addClass(sidebar, 'collapsed');

      const openMenus = sidebar.querySelectorAll<HTMLElement>('.collapse.show');
      openMenus.forEach(menu => {
        this.renderer.removeClass(menu, 'show');
        this.renderer.removeStyle(menu, 'height');
      });

      const expanded = sidebar.querySelectorAll<HTMLElement>('[aria-expanded="true"]');
      expanded.forEach(el => this.renderer.setAttribute(el, 'aria-expanded', 'false'));
    } else {
      this.renderer.removeClass(sidebar, 'collapsed');
    }
  }

  /** Toggle a top-level menu */
  toggleMenu(menuId: string) {
    this.posSettingsService.useQboForCheckPrinting$
      .pipe(take(1))
      .subscribe(value => {
        this.useQboForCheckPrinting = value;
      });

    const sidebar = this.el.nativeElement.querySelector('#sidebar') as HTMLElement | null;
    if (!sidebar) return;

    const menu = sidebar.querySelector('#' + menuId) as HTMLElement | null;
    if (!menu) return;

    const isOpen = menu.classList.contains('show');

    // close all menus
    this.closeAllMenus();

    if (!isOpen) {
      this.renderer.addClass(menu, 'show');
      this.renderer.setStyle(menu, 'height', 'auto');
      this.openMenus.add(menuId);
    } else {
      this.openMenus.delete(menuId);
    }
  }

  /** Toggle submenu under a specific parent */
  toggleSubMenu(subId: string, parentId: string) {
    const sidebar = this.el.nativeElement.querySelector('#sidebar') as HTMLElement | null;
    if (!sidebar) return;

    const subMenu = sidebar.querySelector('#' + subId) as HTMLElement | null;
    if (!subMenu) return;

    const isOpen = subMenu.classList.contains('show');

    // close all submenus inside the same parent
    const parent = sidebar.querySelector('#' + parentId) as HTMLElement | null;
    if (parent) {
      const openSubs = parent.querySelectorAll<HTMLElement>('.collapse.show');
      openSubs.forEach(menu => {
        this.renderer.removeClass(menu, 'show');
        this.renderer.removeStyle(menu, 'height');
      });
    }

    if (!isOpen) {
      this.renderer.addClass(subMenu, 'show');
      this.renderer.setStyle(subMenu, 'height', 'auto');
      this.openSubMenus.add(subId);
    } else {
      this.openSubMenus.delete(subId);
    }
  }

  /** Close all top-level menus */
  closeAllMenus() {
    const sidebar = this.el.nativeElement.querySelector('#sidebar') as HTMLElement | null;
    if (!sidebar) return;

    const openMenus = sidebar.querySelectorAll<HTMLElement>('.collapse.show');
    openMenus.forEach(menu => {
      this.renderer.removeClass(menu, 'show');
      this.renderer.removeStyle(menu, 'height');
    });
    this.openMenus.clear();
  }
}
